import PropTypes from 'prop-types';
import { useEffect, useRef } from 'react';
import { Card } from 'react-bootstrap';
import AdvanceTableFooter from '../../../../components/common/advance-table/AdvanceTableFooter';
import { useAdvanceTableContext } from '../../../../providers/AdvanceTableProvider';
import PendingListFooterSearch from './PendingListFooterSearch';
import { usePendingSearch } from './PendingSearchContext';
import { PENDING_TABLE_PER_PAGE } from './pendingTableConfig';

/** 仅在用户改搜索词时回到第 1 页；首挂载跳过，避免冲掉驳回返回恢复的页码 */
const PendingListKeywordPageReset = () => {
	const { keyword } = usePendingSearch();
	const { setPageIndex } = useAdvanceTableContext();
	const prevKeywordRef = useRef(keyword);
	useEffect(() => {
		if (prevKeywordRef.current === keyword) return;
		prevKeywordRef.current = keyword;
		try {
			setPageIndex(0);
		} catch {
			// ignore
		}
	}, [keyword, setPageIndex]);
	return null;
};

/** 列表区按当前每页条数预留高度，不足一页时卡片不跟着变矮 */
const PendingListPageMinHeight = ({ footerRef }) => {
	const { getState } = useAdvanceTableContext();
	const pageSize = Number(getState()?.pagination?.pageSize) || PENDING_TABLE_PER_PAGE;

	useEffect(() => {
		const footer = footerRef.current;
		if (!footer) return undefined;
		const root = footer.closest('.tab-pane') || footer.closest('.processing-order-card');
		if (!root) return undefined;

		const apply = () => {
			const size = Number.isFinite(pageSize) && pageSize > 0 ? pageSize : PENDING_TABLE_PER_PAGE;
			root.style.setProperty('--pending-list-page-size', String(size));
			const rows = [...root.querySelectorAll('.table-responsive tbody tr')];
			if (rows.length >= size) {
				const avg = rows.reduce((sum, row) => sum + row.getBoundingClientRect().height, 0) / rows.length;
				if (avg > 1) root.style.setProperty('--pending-list-row-h', `${avg}px`);
			}
			const headRow = root.querySelector('.table-responsive thead tr');
			if (headRow) {
				const headH = headRow.getBoundingClientRect().height;
				if (headH > 1) root.style.setProperty('--pending-thead-h', `${headH}px`);
			}
		};

		apply();
		const rafId = requestAnimationFrame(apply);
		const tableWrap = root.querySelector('.table-responsive');
		const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(apply) : null;
		if (tableWrap) observer?.observe(tableWrap);
		window.addEventListener('resize', apply);
		return () => {
			cancelAnimationFrame(rafId);
			observer?.disconnect();
			window.removeEventListener('resize', apply);
		};
	}, [footerRef, pageSize]);

	return null;
};

/** 待办表底栏：左信息、中跨 Tab 搜索、右分页 */
const PendingListTableFooter = ({ showTableControls = true }) => {
	const footerRef = useRef(null);

	return (
		<Card.Footer ref={footerRef} className='pending-list-table-footer'>
			<PendingListKeywordPageReset />
			<PendingListPageMinHeight footerRef={footerRef} />
			<AdvanceTableFooter
				rowInfo={showTableControls}
				pageNumberNav={showTableControls}
				rowsPerPageSelection={showTableControls}
				centerPageNav
				trailing={<PendingListFooterSearch />}
			/>
		</Card.Footer>
	);
};

PendingListTableFooter.propTypes = {
	showTableControls: PropTypes.bool,
};

export default PendingListTableFooter;
